// src/screens/GestionAmenidadesScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, onSnapshot, doc, updateDoc, where, getDocs, addDoc } from 'firebase/firestore';
import './GestionAmenidadesScreen.css';
import { IoPencil, IoConstruct, IoPlay, IoAdd, IoBusinessOutline, IoLeafOutline, IoImagesOutline } from 'react-icons/io5';
import { useAuth } from '../context/AuthContext';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function GestionAmenidadesScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [amenidades, setAmenidades] = useState([]);
  const [loading, setLoading] = useState(true);

  // 1. Cargar la lista de todas las amenidades
  useEffect(() => {
    if (!userData?.condominioId) return;

    const condominioId = userData.condominioId;
    const q = query(collection(db, "amenidades"), where("condominioId", "==", condominioId));

    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const lista = [];
      querySnapshot.forEach((doc) => {
        lista.push({ ...doc.data(), id: doc.id });
      });

      // --- LOGICA DE MIGRACIÓN AUTOMÁTICA PARA TEST ---
      if (lista.length === 0 && condominioId !== "CONDO_XYZ") {
        console.log("Condominio sin amenidades. Intentando clonar desde CONDO_XYZ para testing...");
        try {
          const qSource = query(collection(db, "amenidades"), where("condominioId", "==", "CONDO_XYZ"));
          const sourceSnap = await getDocs(qSource);
          if (!sourceSnap.empty) {
            const promises = sourceSnap.docs.map(docSrc => {
              const data = docSrc.data();
              return addDoc(collection(db, "amenidades"), {
                ...data,
                condominioId: condominioId
              });
            });
            await Promise.all(promises);
            console.log(`¡Se clonaron ${promises.length} amenidades de prueba!`);
            return;
          }
        } catch (e) {
          console.error("Error en migración automática:", e);
        }
      }
      // ------------------------------------------------

      setAmenidades(lista);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar amenidades: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userData]);

  // 2. Función para alternar el bloqueo de mantenimiento
  const handleToggleBloqueo = async (amenidad) => {
    const estadoActual = amenidad.bloqueadaPorAdmin || false;
    const nuevoEstado = !estadoActual;

    const confirmar = window.confirm(
      `¿Estás seguro de que quieres ${nuevoEstado ? 'BLOQUEAR (Mantenimiento)' : 'RE-ACTIVAR'} la amenidad "${amenidad.nombre}"?`
    );

    if (confirmar) {
      try {
        const docRef = doc(db, "amenidades", amenidad.id);
        await updateDoc(docRef, {
          bloqueadaPorAdmin: nuevoEstado
        });
      } catch (e) {
        console.error("Error al actualizar: ", e);
        alert("Error al cambiar el estado de la amenidad.");
      }
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-amenidades" />

      <main className="ks-main">
        <KondoBrandHeader title="Administración" showBackButton={false} />

        <div className="ks-scroll-area">
          <div className="amenidad-dashboard-container">
            {/* --- PREMIUM BANNER --- */}
            <div className="muro-header-stitch">
              <div className="muro-hero-card">
                <div className="muro-hero-content">
                  <h1>Amenidades</h1>
                  <p>Configura y administra las áreas comunes disponibles para reserva.</p>
                  
                  <button
                    onClick={() => navigate('/admin-amenidades/crear')}
                    className="muro-hero-action-btn"
                  >
                    <IoAdd size={20} />
                    <span>Nueva Amenidad</span>
                  </button>
                </div>
                <IoLeafOutline className="muro-hero-icon-decor" />
              </div>
            </div>

            {/* --- Main Card Area --- */}
            <div className="amenidad-card">
              {loading && (
                <div className="empty-state">
                  <p>Cargando amenidades...</p>
                </div>
              )}

              {!loading && amenidades.length === 0 && (
                <div className="empty-state">
                  <IoBusinessOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
                  <p>No hay amenidades registradas.</p>
                  <button
                    className="action-btn-primary"
                    style={{ margin: '20px auto' }}
                    onClick={() => navigate('/admin-amenidades/crear')}
                  >
                    <IoAdd size={18} />
                    Crear la primera
                  </button>
                </div>
              )}

              {!loading && amenidades.length > 0 && (
                <div className="amenidades-admin-grid">
                  {amenidades.map((amenidad) => {
                    const estaBloqueada = amenidad.bloqueadaPorAdmin || false;
                    const capacidad = amenidad.reglas?.limiteUsuariosPorReserva || amenidad.capacidadMax || 'N/A';
                    const accesoTag = (amenidad.derechoAcceso && amenidad.derechoAcceso.length > 0 && amenidad.derechoAcceso[0] !== 'todos') || (amenidad.derechosAcceso && amenidad.derechosAcceso.length > 0) ? 'Exclusivo' : 'Público';

                    return (
                      <div key={amenidad.id} className="amenidad-admin-card-item">
                        <div className="amenidad-admin-card-header">
                          {amenidad.fotoUrl ? (
                            <img src={amenidad.fotoUrl} alt="" className="amenidad-admin-image" />
                          ) : (
                            <div className="amenidad-admin-placeholder">
                              <IoImagesOutline size={32} />
                            </div>
                          )}
                          <div className="amenidad-admin-badges">
                            <span className={`status-pill ${estaBloqueada ? 'status-moroso' : 'status-aldia'}`} style={{ backgroundColor: estaBloqueada ? 'rgba(254, 226, 226, 0.95)' : 'rgba(220, 252, 231, 0.95)', color: estaBloqueada ? '#991b1b' : '#166534', border: `1px solid ${estaBloqueada ? '#fecaca' : '#bbf7d0'}`, backdropFilter: 'blur(4px)' }}>
                              {estaBloqueada ? (
                                <><IoConstruct size={14} /> Manten.</>
                              ) : (
                                <><IoPlay size={14} /> Activa</>
                              )}
                            </span>
                          </div>
                        </div>
                        
                        <div className="amenidad-admin-card-body">
                          <h3 className="amenidad-admin-title">{amenidad.nombre}</h3>
                          <p className="amenidad-admin-desc">
                            {amenidad.descripcion ? (amenidad.descripcion.substring(0, 90) + (amenidad.descripcion.length > 90 ? '...' : '')) : 'Sin descripción'}
                          </p>
                          
                          <div className="amenidad-admin-stats">
                            <div className="amenidad-stat-item">
                              <span className="amenidad-stat-label">Aforo máx.</span>
                              <span className="amenidad-stat-value">{capacidad} px</span>
                            </div>
                            <div className="amenidad-stat-item">
                              <span className="amenidad-stat-label">Tipo Acceso</span>
                              <span className="amenidad-stat-value" style={{ color: accesoTag === 'Exclusivo' ? '#7B61FF' : '#4b5563' }}>
                                {accesoTag}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="amenidad-admin-card-footer">
                          <button
                            className="admin-card-btn btn-toggle"
                            title={estaBloqueada ? "Re-Activar" : "Poner en Mantenimiento"}
                            onClick={() => handleToggleBloqueo(amenidad)}
                            style={{ color: estaBloqueada ? '#059669' : '#d97706', backgroundColor: estaBloqueada ? '#ecfdf5' : '#fffbeb' }}
                          >
                            {estaBloqueada ? <><IoPlay size={18} /> Activar</> : <><IoConstruct size={18} /> Mantenimiento</>}
                          </button>

                          <button
                            className="admin-card-btn btn-edit"
                            title="Modificar Configuración"
                            onClick={() => navigate(`/admin-amenidades/modificar/${amenidad.id}`)}
                          >
                            <IoPencil size={18} /> Configurar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <KondoBottomNav activeRoute="/gestion-amenidades" />
    </div>
  );
}

export default GestionAmenidadesScreen;